import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api, fileUrl } from "../lib/api";
import { onEvent } from "../lib/realtime";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n.jsx";
import { PageHeader, StatusPill, Empty, Restricted } from "../components/Bits";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Check, X, FileDown, Pencil, Trash2, Plus, UserPlus } from "lucide-react";
import ReportModal from "./WorkerReportModal";
import ReportEditDialog, { deleteReport } from "../components/ReportEditDialog";
import TimeAllocation from "../components/TimeAllocation";
import ReportFilters, { Pager, presetRange, daysBetween, MAX_DAYS } from "../components/ReportFilters";
import PdfLang from "../components/PdfLang";

export default function Reports() {
  const { t, tn, lang } = useI18n();
  const { can, user } = useAuth();
  const [pdfLang, setPdfLang] = useState(lang || "en");
  useEffect(() => { setPdfLang(lang || "en"); }, [lang]);
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [period, setPeriod] = useState(() => { const [from, to] = presetRange("today"); return { preset: "today", from, to }; });
  const [extra, setExtra] = useState({ employee_id: "", project_id: "", team_id: "" });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [lists, setLists] = useState({ employees: [], projects: [], teams: [] });
  const PAGE_SIZE = 25;
  const [open, setOpen] = useState(null);
  const [edit, setEdit] = useState(null);
  const [create, setCreate] = useState(null); // null | "self" | "other"
  const [wtypes, setWtypes] = useState([]);
  const [units, setUnits] = useState([]);
  const [comment, setComment] = useState("");
  const isOwn = (r) => r?.employee_id === user?.id;
  const canEditR = (r) => r && (isOwn(r) ? can("reports.edit") && ["draft", "submitted", "rejected"].includes(r.status) : can("reports.edit") && user?.effective?.["reports.edit"] !== "self");
  const canDelR = (r) => r && (isOwn(r) ? can("reports.edit") && ["draft", "submitted", "rejected"].includes(r.status) : can("reports.delete") && user?.effective?.["reports.delete"] !== "self");
  const hm = (iso) => (iso ? iso.slice(11, 16) : "—");

  const tooLong = daysBetween(period.from, period.to) > MAX_DAYS;
  const load = () => {
    if (tooLong || !period.from || !period.to) return;
    const qs = new URLSearchParams({ page, page_size: PAGE_SIZE, date_from: period.from, date_to: period.to });
    if (filter) qs.set("status", filter);
    Object.entries(extra).forEach(([k, v]) => v && qs.set(k, v));
    api.get(`/reports?${qs}`).then((r) => { setRows(r.data.items); setTotal(r.data.total); }).catch((e) => toast.error(e.response?.data?.detail || t("error")));
  };
  useEffect(() => { setPage(1); }, [filter, period.from, period.to, extra]);
  useEffect(() => { load(); }, [filter, period.from, period.to, extra, page]); // eslint-disable-line
  useEffect(() => onEvent((ev) => {
    if (!ev.type.startsWith("report.")) return;
    load();
    if (open && ev.report_id === open.id) api.get(`/reports/${open.id}`).then((r) => setOpen(r.data)).catch(() => {});
  }), [filter, period, extra, page, open]); // eslint-disable-line
  useEffect(() => {
    api.get("/config/work-types").then((r) => setWtypes(r.data));
    api.get("/config/units").then((r) => setUnits(r.data));
    api.get("/projects").then((r) => setLists((l) => ({ ...l, projects: r.data }))).catch(() => {});
    if (can("employees.view")) api.get("/employees").then((r) => setLists((l) => ({ ...l, employees: r.data }))).catch(() => {});
    api.get("/config/teams").then((r) => setLists((l) => ({ ...l, teams: r.data }))).catch(() => {});
  }, []); // eslint-disable-line

  const view = async (id) => { const { data } = await api.get(`/reports/${id}`); setOpen(data); setComment(""); };
  const act = async (kind) => {
    try {
      await api.post(`/reports/${open.id}/${kind}`, { comment });
      toast.success(t(kind === "approve" ? "approved" : "rejected"));
      setOpen(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || t("error")); }
  };

  const showFin = can("finance.view");
  const showEff = can("efficiency.view") || isOwn(open);

  return (
    <div>
      <PageHeader title={t("reports")} subtitle={`${total}`} action={
        <div className="flex gap-2 flex-wrap">
          {can("reports.create") && <Button data-testid="create-report-btn" onClick={() => setCreate("self")} className="bg-[var(--amber)] hover:bg-[var(--amber-2)] text-white gap-2"><Plus size={16} /> {t("create_report")}</Button>}
          {can("reports.create_for_others") && <Button data-testid="create-report-for-btn" variant="outline" onClick={() => setCreate("other")} className="border-[var(--amber)] text-[var(--amber)] gap-2"><UserPlus size={16} /> {t("create_report_for")}</Button>}
        </div>} />
      <ReportFilters period={period} setPeriod={setPeriod} status={filter} setStatus={setFilter} extra={extra} setExtra={setExtra}
        employees={lists.employees} projects={lists.projects} teams={lists.teams} />
      {rows.length === 0 ? <Empty /> : (<>
        <div className="md:hidden space-y-2" data-testid="reports-mobile-list">
          {rows.map((r) => (
            <button key={r.id} data-testid={`report-card-${r.id}`} onClick={() => view(r.id)} className="w-full tactical-card p-3 text-left flex items-center gap-3">
              <div className="flex-1 min-w-0"><div className="font-semibold break-words">{r.employee_name}</div><div className="text-xs text-[var(--text-3)]">{r.date} · {r.items_count} {t("items")}</div></div>
              <StatusPill status={r.status} />
            </button>
          ))}
        </div>
        <div className="hidden md:block tactical-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-[var(--text-3)]">
              <th className="p-3">{t("name")}</th><th className="p-3">{t("date")}</th>
              <th className="p-3">{t("work")}</th><th className="p-3">{t("status")}</th><th className="p-3"></th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`report-row-${r.id}`} className="border-b hover:bg-[var(--bg)] cursor-pointer" onClick={() => view(r.id)}>
                  <td className="p-3 font-semibold">{r.employee_name}</td>
                  <td className="p-3">{r.date}</td>
                  <td className="p-3">{r.items_count}</td>
                  <td className="p-3"><StatusPill status={r.status} /></td>
                  <td className="p-3 text-[var(--blue)]">{t("edit")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pager page={page} pageSize={PAGE_SIZE} total={total} onPage={setPage} />
      </>)}

      {open && createPortal(
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setOpen(null)}>
          <div className="bg-white w-full max-w-lg rounded-t-2xl sm:rounded-2xl p-4 sm:p-5 pb-[calc(1.5rem+env(safe-area-inset-bottom))] max-h-[88vh] overflow-y-auto overflow-x-hidden break-words" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-2 mb-1">
              <h3 className="font-display font-bold text-lg min-w-0 break-words">{open.employee_name}</h3>
              <StatusPill status={open.status} />
            </div>
            <div className="text-sm text-[var(--text-3)] mb-3">{open.date}{open.pending_role && <span className="ml-2 text-[var(--amber)] font-semibold" data-testid="pending-step">· {t("pending_step")}: {open.pending_role}</span>}
              {open.shift && <span className="ml-2 font-mono-num" data-testid="report-shift">· {t("shift")}: {hm(open.shift.start_time)}–{hm(open.shift.end_time)} ({open.shift.break_minutes || 0}′) = <b>{open.shift.hours}h</b></span>}
              {open.created_by_name && <div className="text-xs mt-0.5" data-testid="report-created-by">{t("created_by")}: {open.created_by_name}{open.created_for_reason && ` — ${open.created_for_reason}`}</div>}
            </div>
            {(open.form_fields || []).length > 0 && (
              <div className="tactical-card p-3 mb-3 text-sm space-y-1" data-testid="report-custom-fields">
                {open.form_fields.map((f, i) => {
                  const k = f.id || String(i); const v = (open.custom_fields || {})[k];
                  if (v === undefined || v === "" || v === null) return null;
                  const lbl = typeof f.label === "string" ? f.label : tn(f.label);
                  if (f.type === "signature") return <div key={k}><span className="text-[var(--text-3)]">{lbl}:</span><img alt="signature" src={fileUrl(open.signature_file_id || v)} className="h-16 bg-white rounded border mt-1" /></div>;
                  if (f.type === "gps" && open.gps) return <div key={k}><span className="text-[var(--text-3)]">{lbl}:</span> <a className="text-[var(--blue)] underline" target="_blank" rel="noreferrer" href={`https://maps.google.com/?q=${open.gps.lat},${open.gps.lng}`}>{open.gps.lat}, {open.gps.lng}</a></div>;
                  if (f.type === "photo") return <div key={k}><span className="text-[var(--text-3)]">{lbl}:</span><img alt="" src={fileUrl(v)} className="h-20 rounded mt-1 object-cover" /></div>;
                  if (f.type === "file") return <div key={k}><span className="text-[var(--text-3)]">{lbl}:</span> <a className="text-[var(--blue)] underline" href={fileUrl(v)} target="_blank" rel="noreferrer">{t("document")}</a></div>;
                  return <div key={k}><span className="text-[var(--text-3)]">{lbl}:</span> <b>{typeof v === "boolean" ? (v ? "✓" : "✗") : String(v)}</b></div>;
                })}
              </div>
            )}
            {(open.approvals || []).length > 0 && (
              <div className="text-xs text-[var(--text-3)] mb-2 space-y-0.5" data-testid="approvals-list">
                <div className="uppercase font-semibold">{t("approvals")}</div>
                {open.approvals.map((a, i) => <div key={i}>{a.name} — {t(a.action)} {a.step ? `(${a.step})` : ""} {a.comment && `· ${a.comment}`}</div>)}
              </div>
            )}
            <div className="overflow-x-auto mb-3 -mx-1 px-1"><table className="w-full text-sm min-w-[320px]">
              <thead><tr className="text-left text-[var(--text-3)] border-b">
                <th className="py-1">{t("work")}</th><th className="py-1">{t("quantity")}</th>
                <th className="py-1">{t("actual_min_short")}</th>
                {showEff && <th className="py-1">{t("efficiency")}</th>}
                {showFin && <th className="py-1">{t("cost")}</th>}
              </tr></thead>
              <tbody>
                {open.items.map((it) => {
                  const wt = wtypes.find((w) => w.id === it.work_type_id);
                  const un = units.find((u) => u.id === it.unit_id);
                  return (
                    <tr key={it.id} className="border-b" data-testid={`report-item-${it.id}`}>
                      <td className="py-1.5">{it.item_number && <span className="text-xs text-[var(--text-3)] font-mono-num mr-1">{it.item_number}</span>}{tn(it.work_name) || (wt ? tn(wt.name) : "—")}</td>
                      <td className="py-1.5 font-mono-num">{it.quantity} {tn(it.unit_name) || (un ? tn(un.name) : "")}</td>
                      <td className="py-1.5 font-mono-num">{it.actual_minutes ?? "—"}</td>
                      {showEff && <td className="py-1.5 font-mono-num">{it.activity_type === "NON_PRODUCTIVE" ? <span className="text-[var(--text-3)]">{it.norm_usage_pct != null ? `${it.norm_usage_pct}% ${t("norm_usage")}` : t("NON_PRODUCTIVE")}</span> : (it.efficiency != null ? `${it.efficiency}%` : "—")}</td>}
                      {showFin && <td className="py-1.5 font-mono-num">€{it.cost ?? 0}</td>}
                    </tr>
                  );
                })}
              </tbody>
            </table></div>
            <div className="mb-3"><TimeAllocation alloc={open.time_allocation} /></div>
            {!showFin && <div className="mb-2"><Restricted /></div>}
            {(canEditR(open) || canDelR(open)) && (
              <div className="flex gap-2 mb-2">
                {canEditR(open) && <Button variant="outline" onClick={() => setEdit(open)} className="flex-1 gap-1" data-testid="edit-report-btn"><Pencil size={15} /> {t("edit")}</Button>}
                {canDelR(open) && <Button variant="outline" onClick={async () => { if (await deleteReport(open, !isOwn(open), t)) { setOpen(null); load(); } }} className="flex-1 gap-1 text-[var(--red)]" data-testid="delete-report-btn"><Trash2 size={15} /> {t("delete")}</Button>}
              </div>
            )}
            {(can("reports.approve") || can("reports.reject")) && open.status !== "approved" && (
              <>
                <Textarea placeholder={t("comment")} value={comment} onChange={(e) => setComment(e.target.value)} className="mb-2" data-testid="approve-comment" />
                <div className="flex gap-2">
                  {can("reports.reject") && <Button variant="outline" onClick={() => act("reject")} className="flex-1 text-[var(--red)] gap-1" data-testid="reject-btn"><X size={16} /> {t("reject")}</Button>}
                  {can("reports.approve") && <Button onClick={() => act("approve")} className="flex-1 bg-[var(--green)] text-white gap-1" data-testid="approve-btn"><Check size={16} /> {t("approve")}</Button>}
                </div>
              </>
            )}
            <PdfLang value={pdfLang} onChange={setPdfLang} className="mt-2" />
            <Button variant="outline" onClick={() => window.open(`${fileUrl("x").replace("/files/x", `/reports/${open.id}/pdf`)}&lang=${pdfLang}`, "_blank")} className="w-full mt-2 gap-2" data-testid="report-pdf">
              <FileDown size={16} /> PDF
            </Button>
          </div>
        </div>, document.body)}
      {create && <ReportModal forEmployee={create === "other"} onClose={() => setCreate(null)} onSaved={() => { setCreate(null); load(); }} />}
      {edit && <ReportEditDialog report={edit} needReason={!isOwn(edit)} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); view(edit.id); load(); }} />}
    </div>
  );
}
